import { useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Alert, LinearProgress } from '@mui/material';
import { Trash2Icon } from 'lucide-react';
import { useParams } from 'react-router-dom';

import type { Actions } from '@modules/_shared/components/actions-menu/ActionsMenu';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useNavigateWithSearchParams } from '@modules/_shared/hooks/useNavigateWithSearchParams';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { mapEnquiryToContactRequest } from '@modules/_shared/utils/contactRequest';
import { EnquiryBuyerLeadDrawerContent } from '@modules/enquiries/components/EnquiryBuyerLeadDrawerContent';
import {
  DELETE_ENQUIRY,
  GET_ENQUIRY_CONTACT_REQUEST,
  GET_ENQUIRY_TYPE,
} from '@modules/enquiries/enquiriesQueries';
import { VisibilityInboxDrawerContent } from '@modules/visibility-inbox/VisibilityInboxDrawer';
import { useLocale } from '@shared/hooks/locale';
import { getUUID } from '@shared/utils/global-id';

type EnquiryDrawerProps = {
  onRefetch?: () => void;
};

export const EnquiryDrawer: React.FC<EnquiryDrawerProps> = ({ onRefetch }) => {
  const navigate = useNavigateWithSearchParams();
  const { me } = useAppData();
  const { t } = useLocale();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const { enquiryId } = useParams() as { enquiryId: string };
  const id = getUUID(enquiryId);

  const { data, loading, error } = useQuery(GET_ENQUIRY_TYPE, {
    variables: { id },
  });

  const [deleteEnquiry] = useMutation(DELETE_ENQUIRY, {
    onError: setErrorLike,
  });

  const isContactRequest =
    data?.enquiries_by_pk?.broker_id != null ||
    data?.enquiries_by_pk?.team_id != null;

  const handleClose = () => navigate('../');

  const drawerActions: Actions[] = [
    {
      label: t('Delete enquiry'),
      disabled: !me?.is_admin,
      onClick: () => {
        if (
          window.confirm(t('Are you sure you want to delete this enquiry?'))
        ) {
          // Delete enquiry and remove from cache
          deleteEnquiry({
            variables: {
              id,
            },
            onError: setErrorLike,
            update: cache => {
              cache.evict({ id: `enquiries:${id}` });
            },
          }).then(result => {
            if (result.error == null) {
              onRefetch?.();
              // Close drawer
              handleClose();
            }
          });
        }
      },
      icon: <Trash2Icon size={20} />,
    },
  ];

  return (
    <Drawer
      onClose={handleClose}
      title={isContactRequest ? t('Enquiry details') : t('Buyer lead')}
      maxWidth={isContactRequest ? 540 : 1100}
      actions={drawerActions}
      PaperProps={{
        sx: {
          width: { xs: '100vw', lg: '100%' },
        },
      }}
    >
      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          <pre>{JSON.stringify(error, null, 2)}</pre>
        </Alert>
      )}

      {loading && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            width: '100%',
            zIndex: 2000,
          }}
        />
      )}

      {data?.enquiries_by_pk != null ? (
        isContactRequest ? (
          <ContactRequestDrawerContent enquiryId={data.enquiries_by_pk.id} />
        ) : (
          <EnquiryBuyerLeadDrawerContent
            enquiryId={data.enquiries_by_pk.id}
            initialBuyerLeadId={data.enquiries_by_pk.buyer_lead_id}
          />
        )
      ) : null}
      <MutationErrorSnackbar
        onClose={() => setErrorLike(null)}
        error={apolloError}
      />
    </Drawer>
  );
};

const ContactRequestDrawerContent = ({ enquiryId }: { enquiryId: string }) => {
  const { t } = useLocale();

  const { data, error, loading } = useQuery(GET_ENQUIRY_CONTACT_REQUEST, {
    variables: { id: enquiryId },
  });

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  if (loading) {
    return (
      <LinearProgress
        sx={{
          position: 'absolute',
          top: 0,
          width: '100%',
          zIndex: 2000,
        }}
      />
    );
  }

  const enquiry = data?.enquiries_by_pk;

  if (enquiry == null) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t('Enquiry not found')}
      </Alert>
    );
  }

  const contactRequest = mapEnquiryToContactRequest(enquiry);

  return <VisibilityInboxDrawerContent contactRequest={contactRequest} />;
};
